import { FacilityWasteView } from "generated-graphql/graphql";
import { useMemo } from "react";

import { WasteFacilityContextType, WasteFacilityStateContext } from "./WasteFacilityContext";
import { Filter } from "./WasteFacilityFilters";

/**
 * Provider for surfaces that open the WasteStreamDetailsModal outside the
 * per-facility page — e.g. the tenant-wide Waste Streams grid. Supplies the
 * same context shape the per-facility page provides, bounded to the date
 * range the caller already has active (the Waste Streams page header filter)
 * so the modal's internal date-scoped queries (waste line items,
 * destinations, amount aggregates) scan a narrow window of partitions
 * instead of the full WasteLineUnified history.
 *
 * No URL filter sync: tenant pages don't expose facility-scoped filters in
 * the URL. setWasteFacilityState is a no-op for the same reason.
 */
export const TenantWasteFacilityProvider = ({
  facilityId,
  startDate,
  endDate,
  children,
}: {
  facilityId: string;
  /** ISO YYYY-MM-DD — the active header date range, used to bound queries. */
  startDate: string;
  endDate: string;
  children: React.ReactNode;
}) => {
  const value: WasteFacilityContextType = useMemo(() => {
    const filters: Partial<Filter> = {
      startDate,
      endDate,
      view: FacilityWasteView.All,
    };
    return {
      wasteFacilityState: {
        deferredFilters: filters,
        optimisticFilters: filters,
        isPending: false,
      },
      setWasteFacilityState: () => {},
      effectiveWasteFacilityId: facilityId,
      colocationWasteFacility: null,
    };
  }, [facilityId, startDate, endDate]);

  return (
    <WasteFacilityStateContext.Provider value={value}>
      {children}
    </WasteFacilityStateContext.Provider>
  );
};
